import { createContext, useContext, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lock } from "lucide-react";
import api, { apiError } from "@/lib/api";

const Ctx = createContext(null);

export function EditGateProvider({ children }) {
  const [unlocked, setUnlocked] = useState(sessionStorage.getItem("edit_unlocked") === "1");
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // In-memory only. Never persist the admin password in browser storage.
  const passwordRef = useRef(null);

  const requireEdit = (cb, needsPassword = false) => {
    if (unlocked && (!needsPassword || passwordRef.current)) cb(passwordRef.current);
    else { setPending(() => cb); setErr(""); setPw(""); setOpen(true); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/verify-edit", { password: pw });
      sessionStorage.setItem("edit_unlocked", "1");
      setUnlocked(true);
      setOpen(false);
      passwordRef.current = pw;
      setPw("");
      if (pending) pending(pw);
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Ctx.Provider value={{ unlocked, requireEdit }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[min(24rem,calc(100vw-2rem))] rounded-sm" data-testid="edit-gate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-medium">
              <Lock className="h-4 w-4 text-blue-600" strokeWidth={1.5} /> Edit access required
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <DialogDescription data-testid="edit-gate-description" className="text-xs text-zinc-500">Enter the admin password to create, edit, import or delete anything in the app.</DialogDescription>
            <input data-testid="edit-gate-password-input" type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Admin password"
              className="block w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600" />
            {err && <div data-testid="edit-gate-error" className="border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">{err}</div>}
            <button data-testid="edit-gate-submit" type="submit" disabled={busy || !pw}
              className="w-full rounded-sm bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50">
              {busy ? "Verifying…" : "Unlock editing"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}

export const useEditGate = () => useContext(Ctx);
